"use client";

import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { useRef, useState, useTransition } from "react";
import SubscriberCount from "@/components/subscriber-count";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { PendingLabel } from "@/components/ui/pending-label";
import { Text } from "@/components/ui/text";
import { useTimedState } from "@/hooks/use-timed-state";
import { NEWSLETTER_SUBSCRIBER_COUNT } from "@/lib/constants";
import { subscribeToNewsletter } from "@/lib/newsletter";
import { sleep } from "@/lib/sleep";
type Status = "idle" | "loading" | "success";
export default function NewsletterCard() {
  const formAction = process.env.NEXT_PUBLIC_LOOPS_FORM_ACTION;
  const [email, setEmail] = useState("");
  const [succeeded, setSucceeded] = useTimedState(false, 3000);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const inFlight = useRef(false);
  let status: Status = "idle";
  if (succeeded) {
    status = "success";
  } else if (pending) {
    status = "loading";
  }
  const onSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlight.current || status !== "idle") {
      return;
    }
    if (!formAction) {
      setError("Newsletter signup is unavailable. Please try again later.");
      return;
    }
    setError(null);
    inFlight.current = true;
    startTransition(async () => {
      try {
        await Promise.all([subscribeToNewsletter(formAction, email), sleep(2000)]);
        setSucceeded(true);
        setEmail("");
      } catch {
        setError("Couldn't subscribe. Please try again.");
      } finally {
        inFlight.current = false;
      }
    });
  };
  return <div className="flex w-full flex-col items-center justify-center gap-6">
      <div className="flex w-full flex-col">
        <Heading as="h2" className="mb-5" size={3}>
          Interfaces newsletter
        </Heading>
        <Text as="p" variant="prose">
          Subscribe to get notified when a new issue is released and receive
          newsletter-only design engineering resources.
        </Text>
      </div>
      <div className="flex w-full flex-col items-start gap-6">
        <form className="flex w-full items-stretch overflow-hidden rounded-full bg-preview-bg shadow-custom transition-shadow duration-200 ease-out has-[input:focus-visible]:shadow-custom-active" onSubmit={onSubmit}>
          <label className="sr-only" htmlFor="newsletter-email">
            Email address
          </label>
          <input aria-describedby={error ? "newsletter-error" : undefined} aria-invalid={Boolean(error)} autoComplete="email" className="min-w-0 flex-1 text-ellipsis px-5 text-gray-1200 leading-none outline-none transition-colors duration-200 ease-out placeholder:select-none placeholder:text-gray-800" disabled={status === "loading"} id="newsletter-email" inputMode="email" name="email" onChange={e_0 => setEmail(e_0.target.value)} placeholder="your@email.com" required type="email" value={email} />
          <div className="shrink-0 p-1">
            <Button className="relative min-w-28 overflow-hidden" disabled={status === "loading"} rounded size="xl" type="submit" variant="primary">
              <PendingLabel labelKey={status} pending={status === "loading"} spinnerClassName="text-gray-100">
                {status === "success" ? <IconCircleCheck className="size-5 text-gray-100 will-change-transform" /> : "Subscribe"}
              </PendingLabel>
            </Button>
          </div>
        </form>
        {error && <p className="text-pretty text-red-500 text-sm" id="newsletter-error" role="alert">
            {error}
          </p>}
        <SubscriberCount count={NEWSLETTER_SUBSCRIBER_COUNT} />
      </div>
    </div>;
}